









































































































































































import { Component, Prop, Vue } from "vue-property-decorator"
import { Getter, namespace } from "vuex-class"

import { CaseDetails as CaseDetailsType, CaseField, TemplateDetails } from "@/types"

import CaseMergeModal from "@/components/CaseDetails/CaseMergeModal.vue"
import Toolbar, { ToolbarButton } from "@/components/New/Page/PageComponents/Toolbar.vue"

// @ts-ignore
import QRCode from "qrcode"

import api from "@/services/api"
import CaseFile from "@/types/caseFile"
import { CurrentUser } from "@/types/user"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { CaseUser, UserRole, UserRoleDefinition } from "@/types/caseUser"

import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"

const caseStore = namespace("cases")

enum CaseButtons {
	Merge = "merge",
	GenerateQr = "generate-qr",
	ChangeClosed = "change-close",
	SetSensitive = "make-sensitive",
	InsightHide = "hide-from-insights",
	Delete = "delete",
	Close = "close",
	CompleteTriage = "complete-triage"
}

@Component({
	components: {
		"toolbar": Toolbar,
		"case-merge-modal": CaseMergeModal,
		"loader": SkeletonLoader
	}
})
export default class CaseToolbar extends Vue {
	@Prop({ required: true, type: Object })
	public currentCase!: CaseDetailsType

	@Prop({ default: null, type: Object })
	public caseTemplate!: TemplateDetails | null

	@Prop({ default: false, type: Boolean })
	public canEditCurrentCase!: boolean

	@Prop({ default: () => [], type: Array })
	public caseFiles!: CaseFile[]

	@Getter("getMe")
	public me!: CurrentUser

	@Getter("getCaseName") caseName!: string;
	@Getter("getRecordName") recordName!: string;

	@Getter("getIsFeatureEnabled") featureEnabled!: (feature: TenantFeatures) => boolean

	hideFromInsightsModalShown: boolean = false
	markSensitiveModalShown: boolean = false
	deleteCaseModalShown: boolean = false
	mergeModalShown: boolean = false
	qrModalShown: boolean = false
	closeModalShown: boolean = false
	collapsed: boolean = true

	deleteConfirmText: string | null = null

	get caseRecordName (): string {
		return this.currentCase.isCase
			? this.caseName
			: this.recordName
	}

	get recordSlashCase (): string {
		return this.caseName === this.recordName
			? this.caseName
			: `${this.recordName}/${this.caseName}`
	}

	get canMakeClosedChanges (): boolean {
		if (this.caseTemplate && this.caseTemplate.editClosedPermissions && this.me) {
			return this.caseTemplate.editClosedPermissions.length === 0 ||
					this.caseTemplate.editClosedPermissions.some((teamId: any) => this.me.teams.find((team: any) => team.id === teamId))
		}
		return false
	}

	/**
	 * Checks if something is preventing the case from being closed
	 * @return true if all case fields validated, all required actions completed and no user roles require location, false otherwise
	 */
	public get canClose (): boolean {
		return (!this.caseFieldsNotValidated || this.caseFieldsNotValidated.length === 0) && this.caseActionsValidated && !this.userRoleLocationsEmpty
	}

	/**
	 * Gets a human-readable list of users that do not have location set.
	 * @returns Concatenated string listing users that do have location not set If user role location feature is enabled, null otherwise
	 */
	public get userRoleLocationsEmpty (): string | null {
		if (!this.currentCase || !this.currentCase.fields) return null
		if (!this.featureEnabled(TenantFeatures.UserRoleLocation)) return null

		const outstandingUserRoleLocations =
			Object.keys(this.currentCase.fields) // For each field ID
				.map(fieldId => this.currentCase!.fields![fieldId]) // Pull field
				.filter(field => { // Find only user role fields with a value
					return field.field.dataType === "user-role" &&
						field.value != null &&
						field.field.extensions
				})
				.map(field => { // Convert each field to a list of users in which location is not set
					const value = field.value as UserRole
					const userRole = field.field.extensions as UserRoleDefinition

					if (userRole.subroles && userRole.subroles.length > 0) {
						return value.subRoles
							? value.subRoles.map(subRoleValue => {
								if (!subRoleValue.users || subRoleValue.users.length === 0) return []
								const subRole = userRole.subroles!.find(sr => sr.title === subRoleValue.title)
								if (!subRole || !subRole.allowTimeTracking || !subRole.allowLocationTracking) return []

								return this.locationMissingUsers(subRoleValue.users, field.field.title)
							}).flat()
							: []
					} else if (userRole.allowTimeTracking && userRole.allowLocationTracking) {
						return this.locationMissingUsers(value.users, field.field.title)
					}

					return []
				}).flat() // Smoosh list of lists down to one list
		if (outstandingUserRoleLocations.length === 0) return null // No-one missing location

		// Turn to string
		return "Location missing for " + outstandingUserRoleLocations.map(l => `${l.user} (${l.field})`).join(", ")
	}

	get toolbarButtons (): ToolbarButton[] {
		const buttons: ToolbarButton[] = []

		if (!this.currentCase || !this.currentCase.id) return buttons

		if (this.currentCase.isClosed) {
			if (this.canMakeClosedChanges && !this.canEditCurrentCase) {
				buttons.push({
					key: CaseButtons.ChangeClosed,
					icon: "far fa-edit",
					label: "Make changes",
					tooltip: `Make changes to this closed ${this.caseRecordName}`
				})
			}
		} else if (this.currentCase.pendingTriage && !this.currentCase.isClosed && this.canEditCurrentCase &&
				this.featureEnabled(TenantFeatures.Triage) && !this.featureEnabled(TenantFeatures.SidebarCloseButton)) {
			buttons.push({
				key: CaseButtons.CompleteTriage,
				icon: "fa fa-check",
				label: "Complete Triage",
				tooltip: "Complete Triage",
				disabled: !this.canClose
			})
		} else if (this.canEditCurrentCase && !this.featureEnabled(TenantFeatures.SidebarCloseButton)) {
			buttons.push({
				key: CaseButtons.Close,
				icon: "fa-solid fa-box-check",
				label: "Close",
				tooltip: this.closeCaseButtonToolTipTitle,
				disabled: !this.canClose
			})
		}

		buttons.push({
			key: CaseButtons.GenerateQr,
			icon: "far fa-qrcode",
			label: "QR code",
			tooltip: "View QR code"
		})

		if (this.canEditCurrentCase) {
			if (!this.currentCase.isSensitive) {
				buttons.push({
					key: CaseButtons.SetSensitive,
					icon: "far fa-shield",
					label: "Mark Sensitive",
					tooltip: `Mark ${this.caseRecordName} as sensitive`,
					variant: "info"
				})
			}

			buttons.push({
				key: CaseButtons.Merge,
				icon: "far fa-merge",
				label: "Merge into",
				tooltip: `Merge this ${this.caseRecordName} into another ${this.recordSlashCase}`
			})

			if (this.currentCase.hiddenFromInsights) {
				buttons.push({
					key: CaseButtons.InsightHide,
					icon: "far fa-eye",
					label: "Show in insights",
					tooltip: `Show this ${this.caseRecordName} in insights reports`
				})
			} else {
				buttons.push({
					key: CaseButtons.InsightHide,
					icon: "far fa-eye-slash",
					label: "Hide from insights",
					tooltip: `Hide this ${this.caseRecordName} from insights reports`
				})
			}

			buttons.push({
				key: CaseButtons.Delete,
				icon: "far fa-trash",
				label: "Delete",
				tooltip: `Delete ${this.caseRecordName}`,
				variant: "danger"
			})
		}

		return buttons
	}

	public get qrImage () {
		let data: string | undefined

		QRCode.toDataURL(location.href, { width: 300 }, (err: Error | null, dataUrl: string | undefined) => {
			if (err) {
				console.error("Error generating QR code:", err)
				return null
			}

			data = dataUrl
		})

		return data
	}

	private get caseFieldsNotValidated (): CaseField[] {
		if (!this.currentCase || !this.currentCase.fields) {
			return []
		}

		let allFields: CaseField[] = []
		if (this.currentCase && this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (this.currentCase) {
					allFields.push(this.currentCase.fields![fieldId])
				}
			})
		}

		return allFields.filter(field => this.fieldRequired(field))
	}

	private get caseActionsValidated (): boolean {
		if (!this.currentCase || !this.currentCase.actions) {
			return true
		}

		const isCase: boolean = this.currentCase.isCase
		return !this.currentCase.actions.some(action => ((action.requiredRecord && !isCase) || (action.requiredCase && isCase)) &&
															action.completedBy === null)
	}

	public get closeCaseButtonToolTipTitle (): string {
		if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0 && !this.caseActionsValidated) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " and actions to be completed."
		} else if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " to be completed."
		} else if (!this.caseActionsValidated) {
			return "All field actions required"
		} else if (this.userRoleLocationsEmpty) {
			return this.userRoleLocationsEmpty
		}

		return `Close ${this.caseRecordName}`
	}

	async toolbarClick (buttonKey: CaseButtons) {
		switch (buttonKey) {
			case CaseButtons.ChangeClosed:
				this.$emit("editClosed")
				break
			case CaseButtons.Merge:
				this.mergeModalShown = true
				break
			case CaseButtons.GenerateQr:
				this.qrModalShown = true
				break
			case CaseButtons.SetSensitive:
				this.markSensitiveModalShown = true
				break
			case CaseButtons.InsightHide:
				if (this.currentCase.hiddenFromInsights) {
					await this.hideFromInsights(false)
					break
				}
				this.hideFromInsightsModalShown = true
				break
			case CaseButtons.Delete:
				this.deleteCaseModalShown = true
				break
			case CaseButtons.Close:
				if (this.canClose) {
					this.closeModalShown = true
				}
				break
			case CaseButtons.CompleteTriage:
				if (this.currentCase) {
					this.$emit("completeTriage")
				}
				break
		}
	}

	public async hideFromInsights (hide: boolean) {
		if (!this.currentCase.id) {
			return
		}

		this.$set(this.currentCase!, "hiddenFromInsights", hide)
		await api.saveCase(this.currentCase)

		this.hideFromInsightsModalShown = false
	}

	public async deleteCase () {
		if (this.currentCase.id &&
			((this.currentCase.title
				? this.deleteConfirmText === this.currentCase.title
				: this.deleteConfirmText === "delete"))
		) {
			await api.deleteCase(this.currentCase.id!)

			// Navigate to dashboard
			await this.$router.push("/cases-new")
		}
	}

	public async markSensitive () {
		this.$emit("markSensitive")
		this.markSensitiveModalShown = false
	}

	/**
	 * @returns false if the field is valid, true if field is inaccessible, not required, or has a value set
	 */
	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.caseFiles.filter(caseFile => caseFile.fieldId === id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	private confirmClose () {
		this.$emit("closeCase")
		this.closeModalShown = false
	}

	completeTriage () {
		if (!this.currentCase) return
		api.completeTriage(this.currentCase.id!)
	}

	/**
	 * Pulls users missing locations from user role field user list
	 * @param users user list from either top level or a sub-role (or null)
	 * @param fieldTitle Title from user role field
	 */
	private locationMissingUsers (users: CaseUser[] | null, fieldTitle: string): { field: string, user: string }[] {
		return users ? users
			.filter((u: CaseUser) => u.location === null || u.location === undefined)
			.map((u: CaseUser) => {
				return { field: fieldTitle, user: u.name }
			})
			: []
	}
}
